import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { CompanyApi, JpkExportBodyApi } from "../../../../../../../../../services/Api/Organization/types";

interface Props {
	form: UseFormReturn<JpkExportBodyApi>;
	companyDetails: CompanyApi;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<any>[]>>;
}

const JpkExportDetailsForm = ({ form, companyDetails, setErrors }: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		register,
		watch,
	} = form;

	const formInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	return (
		<>
			<h5 className="mb-2">{t("modules.jpk_export.field.jpk_data.title")}</h5>
			<span className="text-muted">{t("modules.jpk_export.field.jpk_data.description")}</span>
			<fieldset className="form-group mt-3">
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="default_currency_code"
							errors={errors}
							register={register}
							defaultValue={companyDetails.currency}
							label={t("modules.jpk_export.field.default_currency_code.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="full_name"
							errors={errors}
							register={register}
							defaultValue={companyDetails.company_name}
							label={t("modules.jpk_export.field.full_name.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="tax_office_code"
							errors={errors}
							defaultValue={companyDetails.tax_office_code}
							register={register}
							label={t("modules.jpk_export.field.tax_office_code.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="tax_id_no"
							errors={errors}
							defaultValue={companyDetails.tax_id_no}
							register={register}
							label={t("lib:common.word.tax_id_no.title")}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="country_code"
							defaultValue={companyDetails.country_code}
							errors={errors}
							register={register}
							label={t("modules.jpk_export.field.country_code.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="business_register_no"
							errors={errors}
							defaultValue={companyDetails.business_register_no}
							register={register}
							label={t("modules.jpk_export.field.business_register_no.title")}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="voivodeship"
							errors={errors}
							register={register}
							defaultValue={companyDetails.voivodeship}
							label={t("modules.jpk_export.field.voivodeship.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="street"
							errors={errors}
							register={register}
							defaultValue={companyDetails.address?.street}
							label={t("lib:common.address.street")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="district"
							errors={errors}
							defaultValue={companyDetails.district}
							register={register}
							label={t("modules.jpk_export.field.district.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="build_nr"
							errors={errors}
							defaultValue={companyDetails.address?.build_nr}
							register={register}
							label={t("lib:common.address.build_nr")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="community"
							errors={errors}
							register={register}
							defaultValue={companyDetails.community}
							label={t("modules.jpk_export.field.community.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="flat_nr"
							errors={errors}
							register={register}
							defaultValue={companyDetails.address?.flat_nr}
							label={t("lib:common.address.flat_nr")}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6">
						<FormInput
							name="post_office"
							errors={errors}
							register={register}
							defaultValue={companyDetails.post_office}
							label={t("modules.jpk_export.field.post_office.title")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
					<div className="col-md-6">
						<FormInput
							name="city"
							errors={errors}
							register={register}
							label={t("lib:common.address.city")}
							defaultValue={companyDetails.address?.city}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6" />
					<div className="col-md-6">
						<FormInput
							name="zip_code"
							errors={errors}
							register={register}
							defaultValue={companyDetails.address?.zip_code}
							label={t("lib:common.address.zip_code")}
							customValidationConfig={formInputValidationConfig}
						/>
					</div>
				</div>
			</fieldset>
		</>
	);
};

export default JpkExportDetailsForm;
